import { Component, createRef } from "react";
import PropTypes from "prop-types";
import panzoom from "panzoom";
import { propOr } from "ramda";
import debounce from "lodash-es/debounce";

import EventBus from "~/libs/eventBus";

const getHeight = el => el.clientHeight;
const getWidth = el => el.clientWidth;
const getBoundingClientRect = el => el.getBoundingClientRect();

const FACTOR = 1.25;

class ZoomController extends Component {
  static whyDidYouRender = false;

  constructor(props) {
    super(props);

    this.onZoomToRefit = this.onZoomToRefit.bind(this);
    this.onZoomToFit = this.onZoomToFit.bind(this);
    this.onZoomToFitAgain = this.onZoomToFitAgain.bind(this);
    this.getParent = this.getParent.bind(this);
    this.getChild = this.getChild.bind(this);
    this.centerOn = this.centerOn.bind(this);
    this.getTransform = this.getTransform.bind(this);
    this.getScale = this.getScale.bind(this);
    this.pause = this.pause.bind(this);
    this.resume = this.resume.bind(this);
    this.zoomToFit = this.zoomToFit.bind(this);
    this.zoomToRefit = this.zoomToRefit.bind(this);
    this.zoomToFitAgain = this.zoomToFitAgain.bind(this);
    this.zoomIn = this.zoomIn.bind(this);
    this.zoomOut = this.zoomOut.bind(this);
    this.getController = this.getController.bind(this);
    this.renderChildren = this.renderChildren.bind(this);
    this.moveTo = this.moveTo.bind(this);

    EventBus.addListener("zoomToRefit", this.onZoomToRefit);
    EventBus.addListener("zoomToFit", this.onZoomToFit);
    EventBus.addListener("zoomToFitAgain", this.onZoomToFitAgain);
  }

  isFit = false;
  params = { direction: "", padding: {} };

  zoomParentRef = createRef();
  zoomChildRef = createRef();
  zoomInstance = null;

  state = {
    // We use this scale only the rerender the controller on every transformation
    scale: 0
  };

  componentDidMount() {
    this.zoomInstance = panzoom(this.zoomChildRef.current, {
      minZoom: 0.1,
      maxZoom: 8,
      bounds: true,
      boundsPadding: 1,
      smoothScroll: false,
      filterKey: () => true,
      zoomDoubleClickSpeed: 1,
      // disable stopping propagation for flagged elements
      onTouch: e => e.target.closest(".panzoomAllowTouch") === null
    });

    window.addEventListener("resize", this.debouncedZoomToRefit);
    this.zoomInstance.on("transform", this.onTransform);

    this.zoomToRefit();
  }

  componentWillUnmount() {
    this.zoomInstance.dispose();

    EventBus.removeListener("zoomToRefit", this.onZoomToRefit);
    EventBus.removeListener("zoomToFit", this.onZoomToFit);
    EventBus.removeListener("zoomToFitAgain", this.onZoomToFitAgain);
    window.removeEventListener("resize", this.debouncedZoomToRefit);
    this.zoomInstance.off("transform", this.onTransform);
  }

  onZoomToRefit(event) {
    if (this.props.channel === event.detail.channel) {
      this.params = event.detail.params;
      this.zoomToRefit();
    }
  }

  onZoomToFit(event) {
    if (this.props.channel === event.detail.channel) {
      const { direction, padding } = event.detail.params;
      this.zoomToFit(direction, padding);
    }
  }

  onZoomToFitAgain(event) {
    if (this.props.channel === event.detail.channel) {
      this.zoomToFitAgain();
    }
  }

  debouncedZoomToRefit = debounce(
    () => {
      setTimeout(() => {
        this.zoomToRefit();
      }, 100);
    },
    250,
    { trailing: true }
  );

  onTransform = debounce(event => {
    this.setState(state => {
      const scale = event.getTransform().scale;

      if (state.scale !== scale) {
        this.isFit = false;
        return { scale };
      }
    });
  }, 250);

  getParent() {
    return this.zoomParentRef.current;
  }

  getChild() {
    return this.zoomChildRef.current;
  }

  centerOn() {
    return this.zoomInstance.centerOn(this.getChild());
  }

  getTransform() {
    if (this.zoomInstance) {
      return this.zoomInstance.getTransform();
    }

    return { x: 0, y: 0, scale: 1 };
  }

  getScale() {
    return this.getTransform().scale;
  }

  pause() {
    return this.zoomInstance.pause();
  }

  resume() {
    return this.zoomInstance.resume();
  }

  zoomToFit(direction, padding) {
    const paddingTop = propOr(0, "top", padding);
    const paddingRight = propOr(0, "right", padding);
    const paddingBottom = propOr(0, "bottom", padding);
    const paddingLeft = propOr(0, "left", padding);

    this.isFit = true;
    this.params = { direction, padding };

    const parent = this.getParent();
    const child = this.getChild();

    if (!parent) {
      console.warn("no valid parent reference found");
      return;
    }

    if (!child) {
      console.warn("no valid child reference found");
      return;
    }

    if (!this.zoomInstance) {
      console.warn("no valid panzoom instance found");
      return;
    }

    const parentHeight = getHeight(parent) - paddingTop - paddingBottom;
    const childHeight = getHeight(child);

    const parentWidth = getWidth(parent) - paddingLeft - paddingRight;
    const childWidth = getWidth(child);

    const scale = Math.min(parentWidth / childWidth, parentHeight / childHeight);

    console.log("parentHeight", {
      parentHeight,
      childHeight,
      parentWidth,
      childWidth
    });

    const fitbyHeight = scale === parentHeight / childHeight;

    let x, y;

    switch (direction) {
      case "top":
        x = parentWidth / 2 - (childWidth * scale) / 2;
        y = 0;
        break;

      case "left":
        x = 0;
        y = parentHeight / 2 - (childHeight * scale) / 2;
        break;

      case "bottom":
        x = parentWidth / 2 - (childWidth * scale) / 2;
        y = parentHeight - childHeight * scale;
        break;

      case "right":
        x = parentWidth - childWidth * scale;
        y = parentHeight / 2 - (childHeight * scale) / 2;
        break;

      case "top-left":
        x = 0;
        y = 0;
        break;

      case "top-right":
        x = parentWidth - childWidth * scale;
        y = 0;
        break;

      case "bottom-left":
        x = 0;
        y = parentHeight - childHeight * scale;
        break;

      case "bottom-right":
        x = parentWidth - childWidth * scale;
        y = parentHeight - childHeight * scale;
        break;

      case "center":
      default:
        x = parentWidth / 2 - (childWidth * scale) / 2;
        y = parentHeight / 2 - (childHeight * scale) / 2;
    }

    if (fitbyHeight) {
      x = Math.max(x, paddingLeft);
      y += paddingTop;
    } else {
      x += paddingLeft;
      y = Math.max(y, paddingTop);
    }

    this.setState(state => {
      const scale = this.getScale();

      if (state.scale !== scale) {
        return { scale };
      }
    });

    console.log("scale", scale);

    this.zoomInstance.zoomAbs(0, 0, scale);
    this.zoomInstance.moveTo(x, y);
  }

  moveTo(x, y) {
    this.zoomInstance.moveTo(x, y);
  }

  zoomToRefit() {
    if (this.isFit) {
      this.zoomToFit(this.params.direction, this.params.padding);
    }
  }

  zoomToFitAgain() {
    this.zoomToFit(this.params.direction, this.params.padding);
  }

  zoomIn() {
    const parent = this.getParent();
    const parentRect = getBoundingClientRect(parent);

    const x = parentRect.width / 2;
    const y = parentRect.height / 2;

    this.zoomInstance.zoomTo(x, y, FACTOR);
    this.isFit = false;
  }

  zoomOut() {
    const parent = this.getParent();
    const parentRect = getBoundingClientRect(parent);

    const x = parentRect.width / 2;
    const y = parentRect.height / 2;

    this.zoomInstance.zoomTo(x, y, 1 / FACTOR);
    this.isFit = false;
  }

  getController() {
    return {
      zoomToFit: this.zoomToFit,
      zoomIn: this.zoomIn,
      zoomOut: this.zoomOut,
      zoomParentRef: this.zoomParentRef,
      zoomChildRef: this.zoomChildRef,
      getScale: this.getScale,
      pause: this.pause,
      resume: this.resume,
      zoomToFitAgain: this.zoomToFitAgain,
      zoomToRefit: this.zoomToRefit,
      moveTo: this.moveTo,
      getTransform: this.getTransform,
      params: this.params
    };
  }

  renderChildren() {
    return this.props.children(this.getController());
  }

  render() {
    return this.renderChildren();
  }
}

ZoomController.propTypes = {
  channel: PropTypes.string.isRequired,
  children: PropTypes.func.isRequired
};

export default ZoomController;
